import { queryUserInfo } from '~/api/index.js';
import { getStorageSync, removeStorageSync, setStorageSync } from '@/utils/storage.js';
import type { UserInfo } from '../../shared/type/UserInfo';

const USER_INFO_STORAGE_KEY = 'userInfo';
const USER_INFO_COOKIE_KEY = 'user_info';

function getUserInfoCookie() {
	return useCookie<string | null>(USER_INFO_COOKIE_KEY, {
		maxAge: 60 * 60 * 24 * 7,
		sameSite: 'lax',
	});
}

function serializeUserInfoForCookie(userInfo: UserInfo) {
	return JSON.stringify({
		userId: userInfo.userId,
		userName: userInfo.userName,
		realName: userInfo.realName,
		userNo: userInfo.userNo,
		telephone: userInfo.telephone,
		merchantStatus: userInfo.merchantStatus ?? null,
		customerCompanyName: (userInfo as UserInfo & { customerCompanyName?: string | null }).customerCompanyName ?? null,
		companyId: (userInfo as UserInfo & { companyId?: number | null }).companyId ?? null,
		companyCode: (userInfo as UserInfo & { companyCode?: string | null }).companyCode ?? null,
		companyName: (userInfo as UserInfo & { companyName?: string | null }).companyName ?? null,
		userType: (userInfo as UserInfo & { userType?: number | null }).userType ?? null,
	});
}

export function getClientStoredUserInfo(): UserInfo | null {
	if (!import.meta.client) return null;

	const userInfo = getStorageSync<UserInfo>(USER_INFO_STORAGE_KEY);
	return userInfo && typeof userInfo === 'object' ? (userInfo as UserInfo) : null;
}

export function getServerUserInfo(): UserInfo | null {
	const userInfoCookie = getUserInfoCookie();
	if (!userInfoCookie.value) return null;

	try {
		return JSON.parse(userInfoCookie.value) as UserInfo;
	} catch (error) {
		console.error('[userCache] parse user_info cookie failed:', error);
		return null;
	}
}

export function setClientUserInfo(userInfo: UserInfo | null) {
	if (!import.meta.client) return;

	const userInfoCookie = getUserInfoCookie();
	if (!userInfo) {
		removeStorageSync(USER_INFO_STORAGE_KEY);
		userInfoCookie.value = null;
		return;
	}

	setStorageSync(USER_INFO_STORAGE_KEY, userInfo);
	userInfoCookie.value = serializeUserInfoForCookie(userInfo);
}

export function clearClientUserInfo() {
	setClientUserInfo(null);
}

export async function fetchAndStoreClientUserInfo(): Promise<UserInfo | null> {
	if (!import.meta.client) return null;

	try {
		const res = await queryUserInfo();
		if (res?.code == 200 && res?.data) {
			setClientUserInfo(res.data as UserInfo);
			return res.data as UserInfo;
		}

		return null;
	} catch (error) {
		console.error('[userCache] queryUserInfo failed:', error);
		return null;
	}
}

export function getUniversalUserInfo(): UserInfo | null {
	if (import.meta.server) {
		return getServerUserInfo();
	}

	return getClientStoredUserInfo();
}
